import { useState, useMemo, useEffect } from 'react';
import { CardRecord, WritingContent } from '../../db/indexedDb';

interface DetailItem {
  summary: string;
  english: string;
  cardId: string;
}

interface TopicRecallContent extends WritingContent {
  details?: DetailItem[];
}

export interface TopicRecallResult {
  ratio: number;
  checkedCount: number;
  totalCount: number;
}

interface TopicRecallCardProps {
  card: CardRecord;
  onEvaluated?: (result: TopicRecallResult) => void;
}

export default function TopicRecallCard({ card, onEvaluated }: TopicRecallCardProps) {
  const [revealed, setRevealed] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>({});

  const topicTag = useMemo(() => {
    const content = (card.content ?? {}) as TopicRecallContent;
    return card.writingMeta?.topicTag || content.topicTag || '未知话题';
  }, [card]);

  const details = useMemo((): DetailItem[] => {
    const content = (card.content ?? {}) as TopicRecallContent;
    return content.details || [];
  }, [card]);

  // 计算勾选比例
  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  const totalCount = details.length;
  const ratio = totalCount > 0 ? checkedCount / totalCount : 0;

  // 卡片切换时重置状态
  useEffect(() => {
    setRevealed(false);
    setExpandedIndex(null);
    setCheckedItems({});
  }, [card.id]);

  // 勾选变化时通知父组件
  useEffect(() => {
    if (revealed && totalCount > 0) {
      onEvaluated?.({ ratio, checkedCount, totalCount });
    }
  }, [checkedItems, revealed, ratio, checkedCount, totalCount, onEvaluated]);

  const toggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <div className="practice-card writing-compact">
      {/* Header */}
      <div className="practice-card-header">
        <span className="practice-topic-tag">话题回忆</span>
        <h3 className="practice-question topic-recall-title">{topicTag}</h3>
        <p className="topic-recall-hint">回忆这个话题的所有要点</p>
      </div>

      {/* 答案区域 */}
      <div className="topic-recall-content">
        {!revealed ? (
          <button
            type="button"
            className="topic-recall-reveal-btn"
            onClick={() => setRevealed(true)}
          >
            显示答案
          </button>
        ) : (
          <>
            <ul className="topic-recall-list">
              {details.map((item, index) => (
                <li key={index} className="topic-recall-item-wrapper">
                  <div className="topic-recall-row">
                    <label className="topic-recall-checkbox">
                      <input
                        type="checkbox"
                        checked={checkedItems[index] ?? false}
                        onChange={(e) => setCheckedItems(prev => ({ ...prev, [index]: e.target.checked }))}
                      />
                      <span className="checkmark" />
                    </label>
                    <button
                      type="button"
                      className={`topic-recall-item ${expandedIndex === index ? 'expanded' : ''}`}
                      onClick={() => toggleExpand(index)}
                    >
                      <span className="topic-recall-text">{item.summary}</span>
                      <span className="topic-recall-expand-icon">
                        {expandedIndex === index ? '▼' : '▶'}
                      </span>
                    </button>
                  </div>
                  {expandedIndex === index && (
                    <div className="topic-recall-english">
                      {item.english}
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <div className="topic-recall-ratio">
              <span>已勾选 {checkedCount}/{totalCount}</span>
              {ratio >= 0.8 && <span className="pill success">建议 GOOD</span>}
              {ratio >= 0.5 && ratio < 0.8 && <span className="pill warn">建议 HARD</span>}
              {ratio < 0.5 && totalCount > 0 && <span className="pill danger">建议 AGAIN</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
